(() => {
  const CONFIG = {
    actionCode: "CourseList",
    apiVersion: "1.1",
    sigVersion: "2.0",
    sigMethod: "HmacSHA256",
    responseFormat: "JSON",

    listId: "igolf__list",
    hintSelector: "[data-hint]",
    inputSelector: ".igolf__input",
    buttonSelector: ".igolf__search-btn",

    geoIpUrl: "https://ipapi.co/json/",
  };

  const $ = (s, root = document) => root.querySelector(s);

  const setHint = (t) => {
    const el = $(CONFIG.hintSelector);
    if (el) el.textContent = t || "";
  };

  const escHtml = (s) =>
    (s ?? "")
      .toString()
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");

  const makeTimestamp = () => {
    const now = new Date();

    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZoneName: "shortOffset",
    }).formatToParts(now);

    const get = (type) => parts.find((p) => p.type === type)?.value || "";

    const year = get("year");
    const month = get("month");
    const day = get("day");
    const hour = get("hour");
    const minute = get("minute");
    const second = get("second");

    let offset = get("timeZoneName").replace("GMT", "").replace("UTC", "");
    const m = offset.match(/^([+-])(\d{1,2})(?::?(\d{2}))?$/);

    if (!m) {
      throw new Error(`Unexpected timezone offset format: ${offset}`);
    }

    const sign = m[1];
    const hh = m[2].padStart(2, "0");
    const mm = (m[3] || "00").padStart(2, "0");

    return `${year}${month}${day}${hour}${minute}${second}${sign}${hh}${mm}`;
  };

  const toSign = (
    actionCode,
    apiKey,
    apiVersion,
    sigVersion,
    sigMethod,
    timestamp,
    responseFormat
  ) =>
    [
      actionCode,
      apiKey,
      apiVersion,
      sigVersion,
      sigMethod,
      timestamp,
      responseFormat,
    ].join("/");

  const base64Url = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
  };

  const signHmacSha256 = async (message, secretKey) => {
    const enc = new TextEncoder();
    const key = await crypto.subtle.importKey(
      "raw",
      enc.encode(secretKey),
      { name: "HMAC", hash: "SHA-256" },
      false,
      ["sign"]
    );
    const sig = await crypto.subtle.sign("HMAC", key, enc.encode(message));
    return base64Url(sig);
  };

  const getCredentials = () => {
    const el = document.getElementById("igolf_api");
    if (!el) throw new Error("Missing #igolf_api config");

    const cfg = JSON.parse(el.textContent || "{}");
    const cred = cfg.credentials || {};

    if (!cred.api_key || !cred.endpoint || !cred.secret_key) {
      throw new Error("Invalid credentials in #igolf_api");
    }

    return cred;
  };

  const buildUrl = (endpoint, signature, timestamp, apiKey) => {
    const base = endpoint.replace(/\/+$/, "");
    return (
      `${base}/${CONFIG.actionCode}` +
      `/${encodeURIComponent(apiKey)}` +
      `/${encodeURIComponent(CONFIG.apiVersion)}` +
      `/${encodeURIComponent(CONFIG.sigVersion)}` +
      `/${encodeURIComponent(CONFIG.sigMethod)}` +
      `/${encodeURIComponent(signature)}` +
      `/${encodeURIComponent(timestamp)}` +
      `/${encodeURIComponent(CONFIG.responseFormat)}`
    );
  };

  const renderList = (courses) => {
    const list = document.getElementById(CONFIG.listId);
    if (!list) return;

    list.innerHTML = "";

    if (!Array.isArray(courses) || courses.length === 0) {
      list.innerHTML = `
        <li class="igolf__empty" role="listitem">
          No courses found
        </li>
      `;
      return;
    }

    const frag = document.createDocumentFragment();

    courses.forEach((c, idx) => {
      const name = (c.courseName ?? c.CourseName ?? "Unnamed course").toString().trim();
      const city = (c.city ?? c.City ?? "").toString().trim();
      const state = (c.stateShort ?? c.State ?? "").toString().trim();
      const zip = (c.zipCode ?? c.Zip ?? c.ZipCode ?? "").toString().trim();
      const hole = (c.layoutHoles ?? c.holes ?? "").toString().trim();

      const location = [city, state].filter(Boolean).join(", ");
      const locationLine = [location, zip].filter(Boolean).join(" ");

      const li = document.createElement("li");
      li.className = `igolf__item ${idx === 0 ? "is-closest" : ""}`;
      li.setAttribute("role", "listitem");
      li.dataset.index = String(idx);

      li.innerHTML = `
        <article class="igolf__item-card">
          <div class="igolf__item-left">
            <div class="igolf__radio" aria-hidden="true"></div>

            <div class="igolf__item-text">
              <h3 class="igolf__item-title">${escHtml(name)}</h3>
              ${locationLine ? `<p class="igolf__item-subtitle">${escHtml(locationLine)}</p>` : ""}
              ${hole ? `<p class="igolf__item-hole">${escHtml(hole)} holes</p>` : ""}
            </div>
          </div>

          <div class="igolf__item-right">
            <span class="igolf__badge">Golf Map Supported</span>
          </div>
        </article>
      `;

      frag.appendChild(li);
    });

    list.appendChild(frag);
  };

  const buildPayload = (query) => {
    const q = (query || "").trim();
    const isZip = /^\d{5}(-\d{4})?$/.test(q);

    const payload = { active: "1" };

    if (q) {
      if (isZip) payload.zipcode = q;
      else payload.city = q;
    }

    return payload;
  };

  const getGeoIP = async () => {
    const res = await fetch(CONFIG.geoIpUrl, {
      headers: { Accept: "application/json" },
      cache: "no-store",
    });

    if (!res.ok) throw new Error("GeoIP failed");

    const d = await res.json();

    return {
      city: d.city || "",
      region: d.region_code || d.region || "",
      postal: d.postal || "",
      lat: d.latitude || null,
      lng: d.longitude || null,
    };
  };

  let inFlight = null;

  const search = async (query) => {
    const run = (async () => {
      try {
        const cred = getCredentials();
        const timestamp = makeTimestamp();
        const toSignStr = toSign(
          CONFIG.actionCode,
          cred.api_key,
          CONFIG.apiVersion,
          CONFIG.sigVersion,
          CONFIG.sigMethod,
          timestamp,
          CONFIG.responseFormat
        );

        const signature = await signHmacSha256(toSignStr, cred.secret_key);
        const url = buildUrl(cred.endpoint, signature, timestamp, cred.api_key);
        const payload = buildPayload(query);

        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        });

        const data = await res.json();
        const courses = data.courseList || [];

        renderList(courses);

        if (!courses.length && query) {
          setHint(`No courses found near "${query}"`);
        } else {
          setHint("");
        }
      } catch (e) {
        console.error("[igolf_search]", e);
        setHint("Could not load courses.");
        renderList([]);
      }
    })();

    inFlight = run;
    await run;
  };

  const autoDetectAndSearch = async () => {
    const input = $(CONFIG.inputSelector);

    try {
      setHint("Detecting location...");

      const loc = await getGeoIP();
      const city = (loc.city || "").trim();
      const postal = (loc.postal || "").trim();

      const keyword = city || postal;

      if (!keyword) {
        setHint("Location unavailable");
        return;
      }

      // if (input && !input.value.trim()) {
      //   input.value = keyword;
      // }

      await search(keyword);
    } catch (e) {
      console.warn("[igolf_location]", e);
      setHint("Location unavailable");
    }
  };

  const bindUI = () => {
    const input = $(CONFIG.inputSelector);
    const btn = $(CONFIG.buttonSelector);

    if (btn) {
      btn.addEventListener("click", () => search(input?.value || ""));
    }

    if (input) {
      input.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          search(input.value || "");
        }
      });
    }
  };

  const init = () => {
    bindUI();
    // autoDetectAndSearch();
  };

  document.readyState === "loading"
    ? document.addEventListener("DOMContentLoaded", init)
    : init();
})();